/**
 * @fileoverview Plan step component for reviewing and customizing the editing plan.
 * When referenceEditingPatterns is provided (Edit Like Me flow), also renders
 * pattern review controls: deletion examples, addition editing, and style insights.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import { TrebbleTitle, TrebbleText, TrebbleParagraph } from '../../common/TrebbleTypography.tsx';
import TrebbleCollapse, { TrebblePanel } from '../../common/TrebbleCollapse.tsx';
import TrebbleCheckbox from '../../common/TrebbleCheckbox.tsx';
import TrebbleCard from '../../common/TrebbleCard.tsx';
import TrebbleInput from '../../common/TrebbleInput.tsx';
import TrebbleSelect from '../../common/TrebbleSelect.tsx';
import TrebbleButton from '../../common/TrebbleButton.tsx';
import deepCutApi from '../services/deepcutApi.js';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';
import TrebbleClientAPI from '../../../models/helper/TrebbleClientAPI';

/**
 * @typedef {import('../types/deepcut.js').AnalysisResults} AnalysisResults
 * @typedef {import('../types/deepcut.js').ProbingQuestion} ProbingQuestion
 */

/**
 * Helper for i18n with fallback
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
const t = (key, fallback) => (window.getI18n && window.getI18n(ti18n, key)) || fallback;

// --- Constants for addition pattern editing ---

/** Fallback classification options when the LLM does not provide per-addition options */
const FALLBACK_CLASSIFICATION_OPTIONS = [
  { value: 'intro_music', label: t('CLASSIFICATION_INTRO_MUSIC', 'Intro Music') },
  { value: 'outro_music', label: t('CLASSIFICATION_OUTRO_MUSIC', 'Outro Music') },
  { value: 'background_music', label: t('CLASSIFICATION_BACKGROUND_MUSIC', 'Background Music') },
  { value: 'transition_sound', label: t('CLASSIFICATION_TRANSITION_SOUND', 'Transition Sound') },
  { value: 'host_intro', label: t('CLASSIFICATION_HOST_INTRO', 'Host Intro') },
  { value: 'guest_presentation', label: t('CLASSIFICATION_GUEST_PRESENTATION', 'Guest Presentation') },
  { value: 'ad_break_bumper', label: t('CLASSIFICATION_AD_BREAK_BUMPER', 'Ad Break Bumper') },
  { value: 'segment_divider', label: t('CLASSIFICATION_SEGMENT_DIVIDER', 'Segment Divider') },
  { value: 'sound_effect', label: t('CLASSIFICATION_SOUND_EFFECT', 'Sound Effect') },
];

/** Fallback position options when the LLM does not provide per-addition options */
const FALLBACK_POSITION_OPTIONS = [
  { value: 'beginning', label: t('POSITION_BEGINNING', 'Beginning of episode') },
  { value: 'end', label: t('POSITION_END', 'End of episode') },
  { value: 'between_segments', label: t('POSITION_BETWEEN', 'Between segments') },
  { value: 'before_guest', label: t('POSITION_BEFORE_GUEST', 'Before guest segment') },
  { value: 'after_ad', label: t('POSITION_AFTER_AD', 'After ad break') },
];

const CUSTOM_OPTION = { value: 'custom', label: t('CLASSIFICATION_CUSTOM', 'Custom...') };

/**
 * Build dropdown options for a single addition from LLM-provided arrays.
 * Falls back to the given fallbackOptions if the LLM didn't provide any.
 * Always appends "Custom..." at the end.
 * @param {Array<{value:string,label:string}>|undefined} llmOptions
 * @param {Array<{value:string,label:string}>} fallbackOptions
 * @returns {Array<{value:string,label:string}>}
 */
function buildDropdownOptions(llmOptions, fallbackOptions) {
  const base = (llmOptions && llmOptions.length > 0) ? llmOptions : fallbackOptions;
  // Avoid duplicate "Custom..." if already present
  const withoutCustom = base.filter(o => o.value !== 'custom');
  return [...withoutCustom, CUSTOM_OPTION];
}

/** Icon map for addition pattern types/classifications */
const ADDITION_ICONS = {
  intro_music: '\u{1F3B6}',
  outro_music: '\u{1F3B5}',
  background_music: '\u{1F3A7}',
  transition_sound: '\u{1F4AB}',
  host_intro: '\u{1F399}',
  guest_presentation: '\u{1F3A4}',
  ad_break_bumper: '\u{1F4E2}',
  segment_divider: '\u2796',
  sound_effect: '\u{1F50A}',
};

/**
 * Returns an icon for an addition classification type
 * @param {string} type
 * @returns {string}
 */
function getAdditionIcon(type) {
  return ADDITION_ICONS[type] || '\u{1F3B6}';
}

// --- Sub-components ---

/**
 * Small inline pagination control for example navigation
 * @param {{ current: number, total: number, onPrev: Function, onNext: Function }} props
 */
function ExamplePager({ current, total, onPrev, onNext }) {
  if (total <= 1) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-[var(--text-default-tooltip-color)]">
      <button
        type="button"
        onClick={onPrev}
        disabled={current <= 0}
        className="bg-transparent border-none cursor-pointer px-1 disabled:opacity-30 text-[var(--text-default-tooltip-color)]"
      >
        {'\u2039'}
      </button>
      <span>{current + 1}/{total}</span>
      <button
        type="button"
        onClick={onNext}
        disabled={current >= total - 1}
        className="bg-transparent border-none cursor-pointer px-1 disabled:opacity-30 text-[var(--text-default-tooltip-color)]"
      >
        {'\u203A'}
      </button>
    </span>
  );
}

/**
 * Inline note input that expands on click
 * @param {{ value: string, onChange: (val: string) => void }} props
 */
function InlineNoteInput({ value, onChange }) {
  const [expanded, setExpanded] = useState(!!value);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="bg-transparent border-none cursor-pointer text-xs text-[var(--trebble-primary)] p-0 mt-2"
      >
        {t('ADD_NOTE_FOR_AI', 'Add a note for the AI...')}
      </button>
    );
  }

  return (
    <div className="mt-2">
      <TrebbleInput
        useEditorTheme
        size="small"
        placeholder={t('ADD_NOTE_FOR_AI', 'Add a note for the AI...')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * Tries to find a deletion pattern that matches a chip label via substring match.
 * @param {string} chipLabel
 * @param {Array} deletionPatterns
 * @returns {Object|null}
 */
function findMatchingDeletionPattern(chipLabel, deletionPatterns) {
  if (!chipLabel || !deletionPatterns || deletionPatterns.length === 0) return null;
  const normalizedLabel = chipLabel.toLowerCase().replace(/[_-]/g, ' ');
  for (const pattern of deletionPatterns) {
    const normalizedType = (pattern.patternType || '').toLowerCase().replace(/[_-]/g, ' ');
    if (normalizedLabel.includes(normalizedType) || normalizedType.includes(normalizedLabel)) {
      return pattern;
    }
  }
  return null;
}

/**
 * Plan step component for reviewing and customizing the editing plan.
 * Optionally renders reference editing pattern controls when in Edit Like Me mode.
 *
 * @param {Object} props
 * @param {AnalysisResults} props.localResults - Current analysis results
 * @param {string} props.targetPlatform - Selected target platform
 * @param {string} props.customPlatform - Custom platform text
 * @param {Object.<string, string>} props.questionAnswers - Answers to probing questions
 * @param {Object.<string, boolean>} props.planSelections - Current plan selections
 * @param {Function} props.onPlanSelectionsChange - Callback for plan selection changes
 * @param {Object|null} [props.referenceEditingPatterns] - Edit Like Me patterns (null for regular DeepCut)
 * @param {Function|null} [props.onPatternsChange] - Callback for modified patterns (null for regular DeepCut)
 * @returns {React.ReactElement}
 */
export default function PlanStep({
  localResults,
  targetPlatform,
  customPlatform,
  questionAnswers,
  planSelections,
  onPlanSelectionsChange,
  referenceEditingPatterns,
  onPatternsChange,
}) {
  // --- Validate required props ---

  if (!localResults) {
    console.warn('PlanStep: localResults is required');
    return null;
  }

  if (!planSelections || typeof onPlanSelectionsChange !== 'function') {
    console.warn('PlanStep: planSelections and onPlanSelectionsChange are required');
    return null;
  }

  // --- Determine Edit Like Me mode from data ---

  const hasPatterns = !!referenceEditingPatterns;
  const deletionPatterns = referenceEditingPatterns?.deletionPatterns || [];
  const additionPatterns = referenceEditingPatterns?.additionPatterns || [];

  // --- Plan chip selection ---

  const updatePlanSelection = (chipLabel, checked) => {
    onPlanSelectionsChange({
      ...planSelections,
      [chipLabel]: checked,
    });
  };

  // --- Reference pattern state (only meaningful when hasPatterns) ---

  const [enabledAdditions, setEnabledAdditions] = useState(() => {
    if (!hasPatterns) return {};
    const map = {};
    additionPatterns.forEach((_, i) => { map[i] = true; });
    return map;
  });

  const [additionEdits, setAdditionEdits] = useState(() => {
    if (!hasPatterns) return {};
    const map = {};
    additionPatterns.forEach((p, i) => {
      // Use userSelectedType (from cache override) when available, otherwise fall back to Lambda data
      const initialClassification = p.userSelectedType || p.audioClassification?.suggestedUsage || p.type || '';
      map[i] = {
        classification: initialClassification,
        customClassification: '',
        description: p.audioClassification?.description || '',
        position: p.position || '',
        customPosition: '',
      };
    });
    return map;
  });

  const [additionNotes, setAdditionNotes] = useState({});
  const [deletionNotes, setDeletionNotes] = useState({});

  // Debounce timers keyed by audioUrl — one timer per audio file so changing
  // different items within 800ms doesn't cancel each other's saves.
  const saveUserSelectionTimerRef = useRef({});

  // Cancel any pending classification saves on unmount so a delayed callback
  // doesn't persist a preference the user never actually kept (e.g. they
  // changed type and immediately left this step).
  useEffect(() => {
    return () => {
      const timers = saveUserSelectionTimerRef.current;
      Object.values(timers).forEach((timerId) => {
        if (timerId) clearTimeout(timerId);
      });
      saveUserSelectionTimerRef.current = {};
    };
  }, []);

  const [expandedItems, setExpandedItems] = useState(new Set());

  const [exRemPage, setExRemPage] = useState({});
  const [exKeptPage, setExKeptPage] = useState({});

  const [exSelections, setExSelections] = useState(() => {
    if (!hasPatterns) return {};
    const map = {};
    deletionPatterns.forEach((p) => {
      const exMap = {};
      (p.examples || []).forEach((_, i) => { exMap[i] = true; });
      map[p.patternType] = exMap;
    });
    return map;
  });

  // --- Audio classification for additions (on mount) ---
  const [audioClassifications, setAudioClassifications] = useState({});

  useEffect(() => {
    if (!hasPatterns || additionPatterns.length === 0) return;

    // Only classify additions that lack audioClassification data
    const needsClassification = additionPatterns.some(
      (p) => !p.audioClassification?.description
    );
    if (!needsClassification) return;

    let cancelled = false;
    deepCutApi.classifyAdditionPatterns(additionPatterns).then((results) => {
      if (cancelled || !results || results.length === 0) return;
      const map = {};
      for (const result of results) {
        if (result.success && result.classification) {
          map[result.audioUrl] = result.classification;
        }
      }
      if (Object.keys(map).length > 0) {
        setAudioClassifications(map);
      }
    });

    return () => { cancelled = true; };
  }, [hasPatterns, additionPatterns.length]);

  /**
   * Get the display description for an addition pattern,
   * preferring classification data over raw pattern data.
   */
  const getAdditionDescription = useCallback((pattern, index) => {
    const edit = additionEdits[index];
    if (edit?.description) return edit.description;
    if (pattern.audioClassification?.description) return pattern.audioClassification.description;
    const classified = audioClassifications[pattern.fileInfo?.audioUrl];
    if (classified?.description) return classified.description;
    return pattern.rationale || '';
  }, [additionEdits, audioClassifications]);

  const getAdditionClassification = useCallback((pattern, index) => {
    const edit = additionEdits[index];
    if (edit?.classification) return edit.classification;
    if (pattern.audioClassification?.suggestedUsage) return pattern.audioClassification.suggestedUsage;
    const classified = audioClassifications[pattern.fileInfo?.audioUrl];
    if (classified?.suggestedUsage) return classified.suggestedUsage;
    return pattern.type || '';
  }, [additionEdits, audioClassifications]);

  /**
   * Get the ranked typeOptions for an addition pattern,
   * preferring LLM-generated options over hardcoded fallbacks.
   * Returns null when no LLM options are available (caller falls back).
   */
  const getAdditionTypeOptions = useCallback((pattern, index) => {
    // Priority: inline typeOptions > audioClassification typeOptions > fetched Gemini result
    if (pattern.typeOptions && pattern.typeOptions.length > 0) return pattern.typeOptions;
    if (pattern.audioClassification?.typeOptions?.length > 0) return pattern.audioClassification.typeOptions;
    const classified = audioClassifications[pattern.fileInfo?.audioUrl];
    if (classified?.typeOptions?.length > 0) return classified.typeOptions;
    if (classified?.classification?.typeOptions?.length > 0) return classified.classification.typeOptions;
    return null;
  }, [audioClassifications]);

  /**
   * Get the ranked positionOptions for an addition pattern,
   * preferring LLM-generated options over hardcoded fallbacks.
   * Returns null when no LLM options are available (caller falls back).
   */
  const getAdditionPositionOptions = useCallback((pattern, index) => {
    if (pattern.positionOptions && pattern.positionOptions.length > 0) return pattern.positionOptions;
    if (pattern.audioClassification?.positionOptions?.length > 0) return pattern.audioClassification.positionOptions;
    const classified = audioClassifications[pattern.fileInfo?.audioUrl];
    if (classified?.positionOptions?.length > 0) return classified.positionOptions;
    if (classified?.classification?.positionOptions?.length > 0) return classified.classification.positionOptions;
    return null;
  }, [audioClassifications]);

  // --- Derived counts ---

  const activeAdditionCount = useMemo(
    () => additionPatterns.filter((_, i) => enabledAdditions[i]).length,
    [additionPatterns, enabledAdditions]
  );

  // --- Toggle helpers ---

  const toggleExpanded = (id) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAddition = (index, checked) => {
    setEnabledAdditions((prev) => ({ ...prev, [index]: checked }));
  };

  const toggleExample = (patternType, exampleIndex, checked) => {
    setExSelections((prev) => ({
      ...prev,
      [patternType]: {
        ...(prev[patternType] || {}),
        [exampleIndex]: checked,
      },
    }));
  };

  const updateDeletionNote = (patternType, value) => {
    setDeletionNotes((prev) => ({ ...prev, [patternType]: value }));
  };

  const updateAdditionNote = (index, value) => {
    setAdditionNotes((prev) => ({ ...prev, [index]: value }));
  };

  const updateAdditionEdit = (index, field, value) => {
    setAdditionEdits((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        [field]: value,
      },
    }));
  };

  // Pager helpers for "removed" examples
  const getRemPage = (patternType) => exRemPage[patternType] || 0;
  const setRemPageFor = (patternType, page) => {
    setExRemPage((prev) => ({ ...prev, [patternType]: page }));
  };

  // Pager helpers for "kept" examples
  const getKeptPage = (patternType) => exKeptPage[patternType] || 0;
  const setKeptPageFor = (patternType, page) => {
    setExKeptPage((prev) => ({ ...prev, [patternType]: page }));
  };

  // --- Output builder: build modified patterns for parent ---

  const buildOutputPatterns = useCallback(() => {
    if (!referenceEditingPatterns) return referenceEditingPatterns;

    const outputDeletions = deletionPatterns
      .map((p) => {
        const note = deletionNotes[p.patternType];
        const exSel = exSelections[p.patternType] || {};
        const filteredExamples = (p.examples || []).filter((_, i) => exSel[i] !== false);

        let guidance = p.pipelineGuidance || '';
        if (note && note.trim()) {
          guidance = guidance + ' [USER INSTRUCTION: ' + note.trim() + ']';
        }

        return {
          ...p,
          pipelineGuidance: guidance,
          examples: filteredExamples,
        };
      });

    const outputAdditions = additionPatterns
      .map((p, i) => ({ pattern: p, originalIndex: i }))
      .filter(({ originalIndex }) => enabledAdditions[originalIndex])
      .map(({ pattern: p, originalIndex }) => {
        const edits = additionEdits[originalIndex] || {};
        const note = additionNotes[originalIndex];

        const result = { ...p };

        if (edits.classification) {
          const classValue = edits.classification === 'custom'
            ? edits.customClassification
            : edits.classification;
          result.audioClassification = {
            ...(p.audioClassification || {}),
            suggestedUsage: classValue,
          };
          result.type = classValue;
        }

        if (edits.description) {
          result.audioClassification = {
            ...(result.audioClassification || {}),
            description: edits.description,
          };
        }

        if (edits.position) {
          result.position = edits.position === 'custom'
            ? edits.customPosition
            : edits.position;
        }

        if (note && note.trim()) {
          result.userNote = note.trim();
        }

        return result;
      });

    return {
      ...referenceEditingPatterns,
      deletionPatterns: outputDeletions,
      additionPatterns: outputAdditions,
    };
  }, [
    referenceEditingPatterns, deletionPatterns, additionPatterns,
    enabledAdditions, deletionNotes, additionNotes,
    exSelections, additionEdits,
  ]);

  // Notify parent when pattern state changes
  useEffect(() => {
    if (hasPatterns && onPatternsChange) {
      onPatternsChange(buildOutputPatterns());
    }
  }, [buildOutputPatterns, onPatternsChange, hasPatterns]);

  // --- Context display flags ---

  const shouldShowContext = false;
  const shouldShowAnswers = false;

  // --- Render helpers for reference pattern examples inside chip panels ---

  /**
   * Renders "Was removed" and "Was kept" example sections for a matching deletion pattern.
   * @param {Object} pattern - The deletion pattern
   * @returns {React.ReactElement|null}
   */
  const renderPatternExamples = (pattern) => {
    if (!pattern) return null;

    const removedExamples = (pattern.examples || []).filter((ex) => ex.type === 'removed');
    const keptExamples = (pattern.examples || []).filter((ex) => ex.type === 'kept');

    if (removedExamples.length === 0 && keptExamples.length === 0) return null;

    const remPage = getRemPage(pattern.patternType);
    const keptPage = getKeptPage(pattern.patternType);

    return (
      <div className="mt-3 border-t border-[var(--border-default-color,#e5e7eb)] pt-3">
        {/* "Was removed" examples */}
        {removedExamples.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <TrebbleText className="text-xs uppercase tracking-wider text-[var(--text-default-tooltip-color)]">
                {t('WAS_REMOVED', 'Was removed')}
              </TrebbleText>
              <ExamplePager
                current={remPage}
                total={removedExamples.length}
                onPrev={() => setRemPageFor(pattern.patternType, Math.max(0, remPage - 1))}
                onNext={() => setRemPageFor(pattern.patternType, Math.min(removedExamples.length - 1, remPage + 1))}
              />
            </div>
            {removedExamples[remPage] && (() => {
              const ex = removedExamples[remPage];
              const globalIdx = (pattern.examples || []).indexOf(ex);
              const exEnabled = (exSelections[pattern.patternType] || {})[globalIdx] !== false;
              return (
                <div className="flex items-start gap-2 bg-[var(--bg-default-hover,#f9fafb)] p-2">
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TrebbleCheckbox
                      useEditorTheme
                      checked={exEnabled}
                      onChange={(e) => toggleExample(pattern.patternType, globalIdx, e.target.checked)}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    {ex.before && (
                      <div className="text-xs">
                        <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)]">
                          {t('BEFORE_LABEL', 'Before')}:
                        </TrebbleText>{' '}
                        <TrebbleText className="text-xs line-through opacity-60">
                          {ex.before}
                        </TrebbleText>
                      </div>
                    )}
                    {ex.after && (
                      <div className="text-xs mt-0.5">
                        <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)]">
                          {t('AFTER_LABEL', 'After')}:
                        </TrebbleText>{' '}
                        <TrebbleText className="text-xs">{ex.after}</TrebbleText>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* "Was kept" examples */}
        {keptExamples.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              <TrebbleText className="text-xs uppercase tracking-wider text-[var(--text-default-tooltip-color)]">
                {t('WAS_KEPT', 'Was kept')}
              </TrebbleText>
              <ExamplePager
                current={keptPage}
                total={keptExamples.length}
                onPrev={() => setKeptPageFor(pattern.patternType, Math.max(0, keptPage - 1))}
                onNext={() => setKeptPageFor(pattern.patternType, Math.min(keptExamples.length - 1, keptPage + 1))}
              />
            </div>
            {keptExamples[keptPage] && (() => {
              const ex = keptExamples[keptPage];
              const globalIdx = (pattern.examples || []).indexOf(ex);
              const exEnabled = (exSelections[pattern.patternType] || {})[globalIdx] !== false;
              return (
                <div className="flex items-start gap-2 bg-[var(--bg-default-hover,#f9fafb)] p-2">
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TrebbleCheckbox
                      useEditorTheme
                      checked={exEnabled}
                      onChange={(e) => toggleExample(pattern.patternType, globalIdx, e.target.checked)}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    {ex.text && (
                      <TrebbleText className="text-xs">{ex.text}</TrebbleText>
                    )}
                    {ex.reason && (
                      <div className="mt-0.5">
                        <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)] italic">
                          {ex.reason}
                        </TrebbleText>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Note input for this deletion pattern */}
        <InlineNoteInput
          value={deletionNotes[pattern.patternType] || ''}
          onChange={(val) => updateDeletionNote(pattern.patternType, val)}
        />
      </div>
    );
  };

  // --- Main render ---

  return (
    <TrebbleSpace direction="vertical" size={8} className="w-full">
      <div className="mb-6">
        <TrebbleTitle level={3} className="m-0">
          {t('PLAN_STEP_TITLE', "Here's our plan - Adjust it to what you want.")}
        </TrebbleTitle>
      </div>

      {/* Platform + Content Type Context */}
      {shouldShowContext && (
        <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 mb-2" style={{ borderRadius: 0 }}>
          <TrebbleText className="text-sm">
            {localResults.detectedContentType && (
              <>{t('OPTIMIZING_CONTENT', 'Optimizing your')} <strong>{localResults.detectedContentType}</strong></>
            )}
            {targetPlatform && localResults.detectedContentType && <> {t('FOR_PLATFORM', 'for')} </>}
            {targetPlatform && (
              <><strong>{targetPlatform === t('PLATFORM_OTHER', 'Other') ? customPlatform : targetPlatform}</strong></>
            )}
          </TrebbleText>
        </div>
      )}

      {/* Strategy summary card */}
      <TrebbleCard
        className="bg-[var(--trebble-primary-50)] dark:bg-[var(--trebble-primary-100)] border-none mb-4 p-4"
      >
        <TrebbleParagraph
          disableDefaultColors
          className="text-gray-900 dark:text-[var(--text-default-tooltip-color)] m-0 text-sm"
        >
          {localResults.strategySummary?.split('\n')[0] || 'No strategy summary available'}
        </TrebbleParagraph>
      </TrebbleCard>

      {/* Style insights (Edit Like Me only) - compact row below strategy summary */}
      {hasPatterns && referenceEditingPatterns?.styleInsights && (
        <div className="mb-4">
          <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)]">
            {referenceEditingPatterns.styleInsights.editingTonePreference && (
              <>{t('TONE_LABEL', 'Tone')}: {referenceEditingPatterns.styleInsights.editingTonePreference}</>
            )}
            {referenceEditingPatterns.styleInsights.editingTonePreference && referenceEditingPatterns.styleInsights.pacingStyle && (
              <> &middot; </>
            )}
            {referenceEditingPatterns.styleInsights.pacingStyle && (
              <>{t('PACING_LABEL', 'Pacing')}: {referenceEditingPatterns.styleInsights.pacingStyle}</>
            )}
            {referenceEditingPatterns.styleInsights.pacingStyle && referenceEditingPatterns.styleInsights.contentPreservation && (
              <> &middot; </>
            )}
            {referenceEditingPatterns.styleInsights.contentPreservation && (
              <>{t('CONTENT_PRESERVATION_LABEL', 'Preserves')}: {referenceEditingPatterns.styleInsights.contentPreservation}</>
            )}
          </TrebbleText>
        </div>
      )}

      {/* Show answered questions summary */}
      {shouldShowAnswers && (
        <TrebbleCard className="bg-gray-50 dark:bg-gray-800 mb-2 p-3">
          <TrebbleText strong className="text-sm mb-2 block">
            {t('YOUR_ANSWERS', 'Your Answers:')}
          </TrebbleText>
          <TrebbleSpace direction="vertical" size="small" className="w-full">
            {localResults.probingQuestions.map((question, index) => {
              const answer = questionAnswers[question.id];
              if (!answer || !answer.trim()) return null;

              return (
                <div key={question.id}>
                  <TrebbleText type="secondary" className="text-sm">
                    {index + 1}. {question.text}
                  </TrebbleText>
                  <br />
                  <TrebbleText className="text-sm ml-3">
                    &rarr; {answer}
                  </TrebbleText>
                </div>
              );
            })}
          </TrebbleSpace>
        </TrebbleCard>
      )}

      {/* Plan chips with optional pattern examples */}
      <TrebbleCollapse
        ghost
        expandIconPosition="end"
        className="bg-transparent mt-2"
      >
        {localResults.plan?.map((chip, index) => {
          // When in Edit Like Me mode, try to find a matching deletion pattern for this chip
          const matchingPattern = hasPatterns
            ? findMatchingDeletionPattern(chip.chipLabel, deletionPatterns)
            : null;

          return (
            <TrebblePanel
              key={chip.chipLabel}
              header={
                <div className="flex items-center gap-2 w-full">
                  <div
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    className="flex items-center"
                  >
                    <TrebbleCheckbox
                      checked={planSelections[chip.chipLabel] || false}
                      onChange={e => {
                        updatePlanSelection(chip.chipLabel, e.target.checked);
                      }}
                      className="mr-2"
                    />
                  </div>
                  <TrebbleText strong className="text-base flex-1">
                    {chip.displayLabel}
                  </TrebbleText>
                </div>
              }
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
              style={{
                marginBottom: index === localResults.plan.length - 1 ? 0 : '1px',
                borderTop: index === 0 ? '1px solid var(--border-default-color, #e5e7eb)' : 'none',
                borderBottom: '1px solid var(--border-default-color, #e5e7eb)',
                borderRadius: 0,
              }}
            >
              <div className="pr-4 pb-2 pl-10">
                <TrebbleText type="secondary" className="text-sm leading-normal">
                  {chip.rationale}
                </TrebbleText>
                {/* Pattern examples (only when a matching deletion pattern exists) */}
                {matchingPattern && renderPatternExamples(matchingPattern)}
              </div>
            </TrebblePanel>
          );
        }) || []}
      </TrebbleCollapse>

      {/* "What to Add" section (Edit Like Me only) */}
      {hasPatterns && additionPatterns.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-3">
            <TrebbleText className="text-xs uppercase tracking-wider text-[var(--text-default-tooltip-color)]">
              {t('WHAT_TO_ADD', 'What to add')}
            </TrebbleText>
            <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)]">
              {activeAdditionCount}/{additionPatterns.length} {t('ACTIVE_COUNT', 'active')}
            </TrebbleText>
          </div>

          <div className="flex flex-col">
            {additionPatterns.map((pattern, index) => {
              const isEnabled = enabledAdditions[index] !== false;
              const isExpanded = expandedItems.has('add-' + index);
              const edits = additionEdits[index] || {};
              const classificationValue = edits.classification || pattern.audioClassification?.suggestedUsage || pattern.type || '';
              const icon = getAdditionIcon(classificationValue);
              const filename = pattern.fileInfo?.title || t('UNKNOWN_FILE', 'Unknown file');
              const aiDescription = edits.description || pattern.audioClassification?.description || '';
              const classificationOptions = buildDropdownOptions(getAdditionTypeOptions(pattern, index), FALLBACK_CLASSIFICATION_OPTIONS);
              const positionOptions = buildDropdownOptions(getAdditionPositionOptions(pattern, index), FALLBACK_POSITION_OPTIONS);

              return (
                <div
                  key={index}
                  className="border-b border-[var(--border-default-color,#e5e7eb)]"
                  style={{
                    borderTop: index === 0 ? '1px solid var(--border-default-color, #e5e7eb)' : 'none',
                    opacity: isEnabled ? 1 : 0.4,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  {/* Collapsed header */}
                  <div
                    className="flex items-start gap-2 py-3 px-2 cursor-pointer select-none"
                    onClick={() => toggleExpanded('add-' + index)}
                  >
                    <div
                      className="flex items-center pt-0.5"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <TrebbleCheckbox
                        useEditorTheme
                        checked={isEnabled}
                        onChange={(e) => toggleAddition(index, e.target.checked)}
                      />
                    </div>
                    <span className="text-base pt-0.5">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <TrebbleText
                        strong
                        className="text-sm"
                        style={!isEnabled ? { textDecoration: 'line-through' } : {}}
                      >
                        {filename}
                      </TrebbleText>
                      <div className="mt-0.5">
                        <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)] leading-snug">
                          {aiDescription}
                        </TrebbleText>
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-2 flex-shrink-0 pt-0.5"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TrebbleSelect
                        useEditorTheme
                        size="small"
                        value={classificationValue}
                        onChange={(value) => {
                          updateAdditionEdit(index, 'classification', value);
                          // Cancel any pending debounced save first
                          const audioUrl = pattern.fileInfo?.audioUrl;
                          if (audioUrl && saveUserSelectionTimerRef.current[audioUrl]) {
                            clearTimeout(saveUserSelectionTimerRef.current[audioUrl]);
                            delete saveUserSelectionTimerRef.current[audioUrl];
                          }
                          // Debounced save to the classification cache (skip 'custom' — no canonical type yet)
                          if (value && value !== 'custom') {
                            if (audioUrl) {
                              const opt = classificationOptions.find(o => o.value === value);
                              const labelToSave = opt?.label || value;
                              saveUserSelectionTimerRef.current[audioUrl] = setTimeout(() => {
                                delete saveUserSelectionTimerRef.current[audioUrl];
                                TrebbleClientAPI.getInstance().saveAudioClassificationUserSelection(
                                  audioUrl, value, labelToSave, navigator.language || 'en'
                                ).catch(err => console.warn('[ELM] Failed to save audio classification user selection:', err));
                              }, 800);
                            }
                          }
                        }}
                        options={classificationOptions}
                        className="w-[140px]"
                        popupMatchSelectWidth={false}
                      />
                    </div>
                    <span className="text-[var(--text-default-tooltip-color)] text-sm pt-1 flex-shrink-0">
                      {isExpanded ? '\u25BE' : '\u25B8'}
                    </span>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="pl-14 pr-4 pb-3">
                      {/* Editable description */}
                      <div className="mb-3">
                        <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)] block mb-1">
                          {t('DESCRIPTION_LABEL', 'Description')}
                        </TrebbleText>
                        <TrebbleInput
                          useEditorTheme
                          size="small"
                          value={edits.description || ''}
                          onChange={(e) => updateAdditionEdit(index, 'description', e.target.value)}
                          placeholder={t('DESCRIBE_THIS_AUDIO', 'Describe this audio...')}
                        />
                      </div>

                      {/* Custom classification input */}
                      {classificationValue === 'custom' && (
                        <div className="mb-3">
                          <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)] block mb-1">
                            {t('DESCRIBE_CUSTOM', 'Describe in your own words...')}
                          </TrebbleText>
                          <TrebbleInput
                            useEditorTheme
                            size="small"
                            value={edits.customClassification || ''}
                            onChange={(e) => updateAdditionEdit(index, 'customClassification', e.target.value)}
                            placeholder={t('DESCRIBE_CUSTOM', 'Describe in your own words...')}
                          />
                        </div>
                      )}

                      {/* Position dropdown */}
                      <div className="mb-3">
                        <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)] block mb-1">
                          {t('INSERT_AT', 'Insert at')}
                        </TrebbleText>
                        <TrebbleSelect
                          useEditorTheme
                          size="small"
                          value={edits.position || pattern.position || ''}
                          onChange={(value) => updateAdditionEdit(index, 'position', value)}
                          options={positionOptions}
                          className="w-full"
                          popupMatchSelectWidth={false}
                        />
                      </div>

                      {/* Custom position input */}
                      {(edits.position || pattern.position) === 'custom' && (
                        <div className="mb-3">
                          <TrebbleText className="text-xs text-[var(--text-default-tooltip-color)] block mb-1">
                            {t('WHERE_TO_INSERT', 'Describe where to insert this audio...')}
                          </TrebbleText>
                          <TrebbleInput
                            useEditorTheme
                            size="small"
                            value={edits.customPosition || ''}
                            onChange={(e) => updateAdditionEdit(index, 'customPosition', e.target.value)}
                            placeholder={t('WHERE_TO_INSERT', 'Describe where to insert this audio...')}
                          />
                        </div>
                      )}

                      {/* Note input */}
                      <InlineNoteInput
                        value={additionNotes[index] || ''}
                        onChange={(val) => updateAdditionNote(index, val)}
                      />

                      {/* Remove button */}
                      <div className="mt-3">
                        <TrebbleButton
                          useEditorTheme
                          size="small"
                          danger
                          onClick={() => toggleAddition(index, false)}
                        >
                          {t('REMOVE_THIS', 'Remove this')}
                        </TrebbleButton>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </TrebbleSpace>
  );
}
